import { useNavigate } from "react-router-dom";
import { getFormattedTextDateWithTime, textCapitalize } from "../../../../helpers/common";
import "./styles.scss";
import { ContentCreationSlugs } from "../../../../types/content-creation";
import { marked, use } from "marked";
import { useEffect, useState } from "react";
import {SlidesRenderer} from "../../../../components/slides/main";

// Utility to clean and convert markdown to JSX-friendly HTML
function convertMarkdownToJSX(line: string): string {

  if (!line || !line.trim()) return '';

  const trimmed = line.trim();

  let tag = 'p'; // Default tag

  // Detect heading or list
  if (trimmed.startsWith('###')) {
    tag = 'h4';
  } else if (trimmed.startsWith('##')) {
    tag = 'h3';
  } else if (trimmed.startsWith('#')) {
    tag = 'h2';
  } else if (trimmed.startsWith('*')) {
    tag = 'li';
  } else if(trimmed.startsWith('**')) {
    tag = 'p';
  }

  const content = cleanInlineMarkdown(
    trimmed.replace(/^#+\s*/, '').replace(/^\*\s*/, '')
  );

  return `<${tag}>${content}</${tag}>`;
}

function cleanInlineMarkdown(text: string): string {
  return text
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')   // **bold**
    .replace(/\*(.*?)\*/g, '<em>$1</em>')               // *italic*
    .replace(/__(.*?)__/g, '<u>$1</u>')                 // __underline__
    .replace(/_(.*?)_/g, '<em>$1</em>')                 // _italic_
    .replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" target="_blank">$1</a>') // [text](url)
    .replace(/"([^"]+)"/g, '<strong>"$1"</strong>')     // quoted text
    .trim();
}

// Map slug to abbreviation
export function getContentAbbrivation(slug: string) {
  switch (slug) {
    case ContentCreationSlugs.PRESSRELEASE:
      return "PR";
    case ContentCreationSlugs.HEALTHBLOG:
      return "Health Blog";
    case ContentCreationSlugs.SOCIALPOST:
      return "Social Post";
    case ContentCreationSlugs.FEATUREARTICLE:
      return "FA";
    case ContentCreationSlugs.MARKETINGEMAILER:
      return "Emailer";
    case ContentCreationSlugs.SCIENTIFICARTICLE:
      return "Scientific Article";
    case ContentCreationSlugs.SCIENTIFICLBL:
      return "Leave Behind Lit.";
    case ContentCreationSlugs.VISUALAID:
      return "Visual Aid";
    default:
      return "";
  }
}

// Main component
export const ShowPressRelease = (props: {
  aiResponse: any;
  pressRelease: any;
  showCreatedBy?: boolean;
  imageLink?: string | undefined;
  index?: number;
  onGenerateMessage?: (slide:any) => void
  isGenerating?: boolean
  isPublic?: boolean,
  onRefresh?:()=>void
}) => {
  const { aiResponse, imageLink, pressRelease, index, showCreatedBy,isPublic,onRefresh } = props;
  const [markedText, setMarkedText] = useState<string>("");
  const [markdownBeforeHeadline, setMarkdownBeforeHeadline] = useState("");
  const [markdownAfterHeadline, setMarkdownAfterHeadline] = useState("");


  const pressReleaseContent = (aiResponse?.article ?? aiResponse?.body)?.split("\n") ?? [];

  const pressReleaseSummary =
    aiResponse?.summary
      ?.split("\n")
      ?.map((line: string) => line.replace(/^\*\s*/, "")) ?? [];

  const pressReleaseReferences =
    aiResponse?.references
      ?.split("\n")
      ?.map((line: string) => line.replace(/^\*\s*/, "")) ?? [];

  let pressReleaseSubheading = (aiResponse?.subheading as string ?? "").trim();
  if (pressReleaseSubheading.startsWith('"') && pressReleaseSubheading.endsWith('"')) {
    pressReleaseSubheading = pressReleaseSubheading.slice(1, -1);
  }

  function isValidUrl(str: string): boolean {
    if (!str.trim()) return false;
    try {
      new URL(str);
      return true;
    } catch {
      return false;
    }
  }

  const navigate = useNavigate();
  const introduction = convertMarkdownToJSX(aiResponse?.introduction ?? ""); 

  useEffect(() => {
  const fetchData = async () => {
    const markdown = aiResponse?.markdown ?? aiResponse?.lbl ?? aiResponse?.va ??"";

    if (!markdown.includes("<headline>")) {
      const afterHtml = await marked(markdown);
      const beforeHtml = await marked(`${pressRelease.headline ? `# ${pressRelease.headline}\n\n` : ""}`);
      setMarkdownBeforeHeadline(beforeHtml);
      setMarkdownAfterHeadline(afterHtml.replace(beforeHtml, "").trim());
      return;
    }

    // Split around <headline> tag
    const [before, rest] = markdown.split("<headline>");
    const [headlineContent, after] = rest.split("</headline>");

    console.log({before, headlineContent, after});

    const beforeHTML = await marked(before.trim());
    const afterHTML = await marked(after.trim());

    setMarkdownBeforeHeadline(beforeHTML);
    setMarkdownAfterHeadline(afterHTML.replace(beforeHTML, "").trim());

  };

  fetchData();
}, [aiResponse]);

  useEffect(() => {
    console.log({markdownBeforeHeadline, markdownAfterHeadline});
  },[markdownBeforeHeadline, markdownAfterHeadline]);
  return (
    <>
    {
      pressRelease?.contentType?.slug === ContentCreationSlugs.VISUALAID && 
      <div
      style={{
        width:"1000px",
      }}
      >
      <SlidesRenderer
        slides={aiResponse}
        onGenerateMessage={props.onGenerateMessage}
        isGenerating={props.isGenerating}
        pdfMode={isPublic}
        pdfLink={pressRelease?.pdfLink}
        onAllGenerated={onRefresh}
      />
      </div>
    }
    {
      pressRelease?.contentType?.slug !== ContentCreationSlugs.VISUALAID && 
      <div {...(index ? { id: `airesponsecontent-${index}` } : {})}>

        {imageLink && (
          <img
            src={imageLink}
            alt="Image"
            className="pressReleaseImg"
          />
        )}

      {(markdownBeforeHeadline || markdownAfterHeadline) ? (
          <>
            <div dangerouslySetInnerHTML={{ __html: markdownBeforeHeadline }} />

            {!!pressRelease?.createdAt && (
              <h6>
                {getContentAbbrivation(pressRelease?.contentType?.slug)} | Updated:{" "}
                {getFormattedTextDateWithTime(pressRelease?.createdAt)}
              </h6>
            )}

            <div dangerouslySetInnerHTML={{ __html: markdownAfterHeadline }} />
          </>
        ) : (
          markedText && <div dangerouslySetInnerHTML={{ __html: markedText }} />
        )}


      {!markdownBeforeHeadline && !markdownAfterHeadline &&
        <>
        {!!pressRelease.headline && (
          <h4>
            <b>{cleanInlineMarkdown((pressRelease.headline ?? "").replace(/^"|"$/g, "").replaceAll("**", ""))}</b>
          </h4>
        )}

        {!!pressReleaseSubheading && (
          <h6>{pressReleaseSubheading}</h6>
        )}

        {!!pressRelease?.createdAt && (
          <h6>
            {getContentAbbrivation(pressRelease?.contentType?.slug)} | Updated:{" "}
            {getFormattedTextDateWithTime(pressRelease?.createdAt)}
          </h6>
        )}

        {showCreatedBy && pressRelease?.createdByDetails?.fullName !== "" && (
          <span className="client-name">
            By {textCapitalize(pressRelease?.createdByDetails?.fullName)}
            {pressRelease?.createdByDetails?.designation && (
              <> &nbsp; (
                {textCapitalize(pressRelease?.createdByDetails?.designation)})
              </>
            )}
          </span>
        )}

        {!!introduction?.trim() && (
        <div dangerouslySetInnerHTML={{ __html: introduction }} />
        )}

        {!!pressReleaseSummary.length && (
          <ul>
            {pressReleaseSummary.map((t: string, key: number) => (
              <li key={`summary_${key}`}>{t}</li>
            ))}
          </ul>
        )}

        {pressReleaseContent.map((line: string, key: number) => {
          const html = convertMarkdownToJSX(line);
          if (!html) return <></>;
          return (
            <div key={`content_${key}`} className="ai-paragraph" dangerouslySetInnerHTML={{ __html: html }} />
          );
        })}

        {!!aiResponse?.conclusion?.trim() && (
          <p className="conclusion">{aiResponse.conclusion}</p>
        )}

        {!!pressReleaseReferences.length && (
          <>
            <h6>References:</h6>
            <ul>
              {pressReleaseReferences
                .filter((t: string) => t.trim())
                .map((t: string, key: number) => {
                  const cleanedText = t.replace(/^\d+\.\s*/, "");

                  return (
                    <li key={`ref_${key}`} style={{ wordBreak: "break-word" }}>
                      {cleanedText.split(/(\bhttps?:\/\/[^\s]+)/gi).map((part, i) => {
                        const isValid = isValidUrl(part);
                        return isValid ? (
                          <a key={i} href={part} target="_blank" rel="noopener noreferrer">
                            {part}
                          </a>
                        ) : (
                          <span key={i}>{part}</span>
                        );
                      })}
                    </li>
                  );
                })}
            </ul>
          </>
        )}
        </>
        }
      </div>
    }
      
    </>
  );
};
